import { Image } from 'expo-image';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import type { AnyComment } from '../../model/types';

import { primum_expert_avatar_badge } from '@/assets/images';
import { Avatar, AvatarFallbackText, AvatarImage } from '@/components/ui/avatar';

/**
 * Anonymous comment and reply support.
 *
 * Anonymous comments hide user identity while maintaining visual differentiation:
 * - Display name shows "Anonymous" instead of user name
 * - User info (state, userType) is hidden
 * - Avatar shows "A" initial with color-coded background
 * - Colors follow the author's alias within the thread when the API sends one, so two
 *   anonymous authors in a thread differ; with no alias they fall back to userId % 6
 * - The same author keeps the same color throughout one thread, and gets an unrelated one
 *   in the next
 * - Colors cycle through 6 brand colors: primary/secondary/tertiary at 400/600 shades
 *
 * Implementation:
 * - isAnonymous flag stored on comment from API
 * - Optimistic comments anonymize user data when isAnonymous is true
 * - Reply-to references show "Anonymous user" when replied-to comment is anonymous (checked via comment's isAnonymous flag)
 * - Color assignment uses userId modulo for consistent mapping
 */
const ANONYMOUS_COLORS = [
  'bg-primary-400',
  'bg-primary-600',
  'bg-secondary-300',
  'bg-secondary-600',
  'bg-tertiary-400',
  'bg-tertiary-600',
] as const;

/**
 * Assigns a color to an anonymous user based on their userId.
 * Uses modulo so the same alias always maps to the same color.
 */
const getAnonymousColor = (fallbackSeed: number, anonymousAlias?: number | null): string => {
  const colorIndex = anonymousAlias
    ? (anonymousAlias - 1) % ANONYMOUS_COLORS.length
    : fallbackSeed % ANONYMOUS_COLORS.length;

  return ANONYMOUS_COLORS[colorIndex];
};

type UserAvatarProps = {
  user: AnyComment['user'];
  initials: string;
  isAnonymous?: boolean;
  anonymousAlias?: number | null;
};

/**
 * Determines avatar background color class.
 * Anonymous users get color-coded backgrounds; LMP users get secondary-500.
 */
const getAvatarBackgroundClass = (
  user: AnyComment['user'],
  isAnonymous: boolean,
  anonymousAlias?: number | null
): string => {
  if (isAnonymous) {
    return getAnonymousColor(user.id, anonymousAlias);
  }

  if (user.userType === 'lmp') {
    return 'bg-secondary-500';
  }

  return '';
};

/**
 * Returns avatar fallback text.
 * Anonymous users show "A" initial; others show initials.
 */
const getAvatarFallbackText = (isAnonymous: boolean, initials: string): string => {
  return isAnonymous ? 'A' : initials;
};

export const UserAvatar = ({
  user,
  initials,
  isAnonymous = false,
  anonymousAlias,
}: UserAvatarProps) => {
  const avatarUrl = user.avatar?.Location128px || user.avatar?.Location;

  const avatarBgClass = getAvatarBackgroundClass(user, isAnonymous, anonymousAlias);

  const fallbackText = getAvatarFallbackText(isAnonymous, initials);

  const showAvatar = !isAnonymous && avatarUrl;

  const showExpertBadge = !isAnonymous && user.userType === 'expert';

  return (
    <View className="relative">
      <Avatar className={twJoin('h-9 w-9', avatarBgClass)}>
        <AvatarFallbackText>{fallbackText}</AvatarFallbackText>
        {showAvatar && <AvatarImage source={{ uri: avatarUrl }} />}
      </Avatar>
      {showExpertBadge && (
        <View className="absolute -right-1 bottom-0 overflow-hidden rounded-full">
          <Image
            source={primum_expert_avatar_badge}
            alt="Primum mark"
            contentFit="contain"
            style={{ height: 16, width: 16 }}
          />
        </View>
      )}
    </View>
  );
};
